import { css } from 'astroturf';
import { StaticQuery, graphql } from 'gatsby';
import React from 'react';
import { ArrowRight } from 'react-bytesize-icons';
import Brow from './Brow';
import SmallCTA from './CTA-small';
import Feature from './Feature';
import FeatureCloud from './FeatureCloud';
import FeatureVideo from './FeatureVideo';
import Testimonials from './Testimonials';
import Link from './link';
import Socials from './Socials';

const styles = css`
  .featureList {
    position: relative;
    padding: 0rem 0 5rem;
    text-align: center;
    scroll-margin-top: 100px;
    color: #0a1530;

    & > h2 {
      font-weight: 900;
      font-size: clamp(18px, 16px + 4vw, 44px);
      line-height: 1.25;
      letter-spacing: -0.015em;
      margin: 4rem auto 0.5rem;
      max-width: 60rem;
      text-wrap: balance;

      padding-left: calc(1rem + env(safe-area-inset-left, 0));
      padding-right: calc(1rem + env(safe-area-inset-right, 0));
    }
    & > span {
      margin-top: 4rem;
    }
    & > span + h2 {
      margin-top: 0;
    }
    & > p {
      font-size: calc(16px + 0.25vw);
      font-weight: 400;
      position: relative;
      max-width: 52rem;
      margin: 0 auto 1.5rem;
      line-height: 1.75;

      padding-left: calc(1rem + env(safe-area-inset-left, 0));
      padding-right: calc(1rem + env(safe-area-inset-right, 0));
      @media (min-width: 770px) {
        font-size: 1.17rem;
      }
    }
  }

  .button {
    position: relative;
    pointer-events: all;
    margin: 0rem 0 0;
    font-size: calc(16px + 0.1vw);
    font-weight: 800;
    color: hsl(200, 56.33%, 55.1%);
    line-height: 1.25;
    padding: 0.33em 0;
    display: inline-block;
    text-decoration: none;
    line-height: 26px;
    transition: all 0.25s ease-in-out;
    border-radius: 0;

    & svg {
      transform: translateX(0);
      transition: 0.2s 0.12s ease-out;
    }

    @media (min-width: 600px) {
      font-size: 1.4rem;
    }

    &:hocus {
      color: #fff;
      text-decoration: none;
      background: none;

      & svg {
        transform: translateX(4px);
      }

      &::after {
        width: 0;
      }
    }

    & svg {
      vertical-align: bottom;
    }
  }

  .primaryButton {
    position: relative;
    pointer-events: all;
    margin: 0;
    font-size: calc(14px + 0.6vw);
    font-weight: 700;
    color: rgba(255, 255, 255, 1);
    background: linear-gradient(135deg, #2c82ad, #67b26f);
    background-image: linear-gradient(135deg, oklab(0.61 -0.08 -0.21), oklab(0.66 -0.24 0.16));
    border-radius: 10px;
    text-align: center;
    display: inline-block;
    text-decoration: none;
    line-height: 50px;
    transition: all 0.25s ease-in-out;
    padding: 0;
    margin-bottom: 1.5rem;
    @media (forced-colors: active) {
      border: 1px solid;
    }

    & i {
      position: absolute;
      top: calc(100% + 10px);
      line-height: 1;
      left: 0;
      right: 0;
      width: 100%;
      text-align: center;
      font-size: 12px;
      font-weight: normal;
      color: #506478;
      background: #f7faff;
      display: block;
      font-style: normal;
      white-space: nowrap;
    }

    & span {
      position: relative;
      z-index: 1;
      content: ' ';
      display: block;
      width: 100%;
      height: 100%;
      padding: 0 30px;
      z-index: 0;
      opacity: 1;
      transition: background 0.25s ease-in-out;
      border-radius: 10px;
      background: #0a1530;
    }

    & svg {
      vertical-align: middle;
      margin-left: 4px;
      transition: transform 0.25s ease-in-out;
    }

    @media (min-width: 600px) {
      & span {
        font-size: 20px;
      }
    }

    &:hocus {
      text-decoration: none;
      & span {
        background: transparent;
      }
      & svg {
        transform: translateX(5px);
      }
    }
  }

  div.socials {
    margin: 1rem auto;
    padding: 0;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 2rem;
    max-width: 40rem;
    font-weight: 400;
    color: #2c82ad;

    max-width: 80rem;
    margin: 2rem auto 0rem;
    font-size: 1rem;

    @media (max-width: 80rem) {
      padding: 0;
    }

    & svg {
      fill: currentcolor;
      flex: 0 0 24px;
      width: 24px;
      height: 24px;
    }
  }

  .pills {
    padding: 0rem 1rem 1rem;
    justify-content: center;
    align-items: center;
    gap: 2rem;
    flex-wrap: wrap;
    display: none;

    @media (min-width: 480px) {
      display: flex;
    }

    & li {
      font-weight: 800;
      display: block;
      background: #fff;
      color: #107db5;
      border: 2px solid;
      transition: 0.25s ease-in-out color;
      border-radius: 4rem;
      padding: 4px 10px;
      box-shadow: none;
      font-size: 1.125rem;
      white-space: nowrap;
    }
  }
`;

function FeatureList() {
  return (
    <StaticQuery
      query={photoquery}
      render={(data) => (
        <section id="benefits" className={styles.featureList}>
          <FeatureVideo screenshot={data.panesVideo} type="light" large brow="responsive design">
            <h3>All the viewports you need.</h3>

            <p>
              Don't manually resize and test websites screen-by-screen. Get all your viewports in one overview, no
              browser resizing needed.
            </p>
            <p>Mobile screens, 5K monitors, all your CSS breakpoints or any size you want.</p>
            <p>
              <Link to="/docs/device-presets/">Device presets docs</Link>
            </p>
          </FeatureVideo>

          <FeatureVideo screenshot={data.syncVideo} type="light" large brow="Synced panes">
            <h3>And they're all in sync.</h3>
            <p>
              Scrolling, clicking, typing and <strong>even hovers</strong> are synced across all viewports.
            </p>
            <p>Don't test viewports or devices one-by-one, check them all at once.</p>
            <p>
              <Link to="/docs/synced-interactions/">Synced interactions docs</Link>
            </p>
          </FeatureVideo>

          <Feature gatsbyImg={data.portal} type="light" full brow="Cross-browser and Cross-device">
            <h3>Even across browsers.</h3>
            <p>Share your local development environment anywhere, on any browser, on any device. </p>
            <p>
              <Link to="/portal/">Polypane Portal</Link> keeps everything in sync whether that's the scroll position,
              interactions, style edits or layout debugging.
            </p>
          </Feature>

          <FeatureVideo screenshot={data.debugtools} type="light" large brow="Debug tools">
            <h3>From A11y to Z-index.</h3>
            <p>
              Apply over 40 different debug tools and simulators to each viewport. Test your site in completely new
              ways.
            </p>
            <p>
              Check out our <Link to="/docs/debug-tools/">Debug tools</Link> and{' '}
              <Link to="/docs/simulators/">Simulators</Link>.
            </p>
          </FeatureVideo>

          <FeatureVideo screenshot={data.metaVideo} type="light" large brow="Meta panel">
            <h3>Social media share card previews.</h3>

            <Socials />
            <p>
              Pixel-perfect previews of social media cards for X/Twitter, Facebook, Slack, LinkedIn, Discord, Telegram,
              Bluesky, Threads and Google Search. In dark and light mode. Even with your localhost.
            </p>

            <p>
              <Link to="/social-media-previews/">Deep dive into our social media previews</Link>
            </p>
          </FeatureVideo>

          <Testimonials />

          <SmallCTA />

          <FeatureVideo screenshot={data.darkmode} full type="light" large brow="Emulation">
            <h3>Dark mode media feature testing.</h3>
            <p>
              No need to change your entire operating system! Test light and dark mode side-by-side in the same view.
              Also works for reduced motion, reduced data and even print styles.
            </p>
            <p>
              <Link to="/docs/emulation/#media">Media emulation docs</Link>
            </p>
          </FeatureVideo>

          {/* <Feature gatsbyImg={data.devex} full type="light" brow="Devtools">
            <h3>Your favorite DevTools extensions.</h3>
            <p>
              Install and use your favorite Chromium devtools, with support for <strong>React DevTools</strong>,{' '}
              <strong>Vue.js DevTools</strong>, <strong>Redux DevTools</strong>, Angular, Svelte and many more.
            </p>
          </Feature> */}

          {/* <FeatureVideo screenshot={data.liveReloadVideo} type="light" brow="zero configuration">
            <h3>Live and hot reloading.</h3>
            <p>
              <strong>Zero configuration</strong> live reloading built right into Polypane for anything from plain old
              HTML to Wordpress to React. <b>Hot-reloading server not needed.</b>
            </p>
            <p>Images and CSS are updated without a page refresh.</p>
          </FeatureVideo> */}

          <h2>&hellip;And that's only scratching the surface.</h2>
          <p>Polypane has everything you need to make awesome web experiences.</p>
          <FeatureCloud type="light" />
          <p>
            <Link className={styles.primaryButton} to="/product-tour/">
              <span>
                Full product tour <ArrowRight width={16} height={16} strokeWidth="5px" />
              </span>
            </Link>
          </p>
        </section>
      )}
    />
  );
}

export default FeatureList;

const photoquery = graphql`
  query FeaturesQuery {
    panesVideo: file(relativePath: { eq: "videos/marketers/devicewall.mp4" }) {
      publicURL
    }
    syncVideo: file(relativePath: { eq: "videos/marketers/syncedactions.mp4" }) {
      publicURL
    }
    debugtools: file(relativePath: { eq: "videos/marketers/debugtools.mp4" }) {
      publicURL
    }
    liveReloadVideo: file(relativePath: { eq: "videos/live-reload.mp4" }) {
      publicURL
    }
    screenshotVideo: file(relativePath: { eq: "videos/marketers/screenshot.mp4" }) {
      publicURL
    }
    metaVideo: file(relativePath: { eq: "videos/marketers/metadata2.mp4" }) {
      publicURL
    }
    darkmode: file(relativePath: { eq: "videos/marketers/darkmode.mp4" }) {
      publicURL
    }
    devex: file(relativePath: { eq: "images/devtools-extensions.png" }) {
      publicURL
      childImageSharp {
        gatsbyImageData(breakpoints: [320, 375, 768, 1024, 1024], quality: 100, layout: FULL_WIDTH)
      }
    }
    portal: file(relativePath: { eq: "images/portal/feature.png" }) {
      publicURL
      childImageSharp {
        gatsbyImageData(breakpoints: [320, 375, 768, 1024, 1024], quality: 100, layout: FULL_WIDTH)
      }
    }
  }
`;
